import styled from "styled-components";
import Tippy, { TippyProps } from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import { DivProps } from "react-html-props";
import { Instance } from "tippy.js";
import { motion } from "motion/react";

const Container = styled.div`
    .content {
        border-radius: 0.75rem;
        border: 2px solid rgba(25, 93, 165, 0.1);
        background: rgba(25, 93, 165, 0.02);
        padding: 1rem 0.75rem;
        transition: 0.2s;

        color: #195da5;
        font-family: Poppins;
        font-size: 1.25rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.5rem;
        letter-spacing: -0.0375rem;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        svg.arrow {
            width: 1.5rem;
            height: 1.5rem;
            color: #195da5;
        }
        &:hover {
            border: 2px solid rgba(25, 93, 165, 0.2);
        }

        &.open {
            border: 2px solid rgba(25, 93, 165, 0.74);
        }
        > span {
            display: flex;
            margin-left: auto;
        }
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    border: 2px solid rgba(25, 93, 165, 0.1);
    background: #fafcfd;
    box-shadow: 0px 20px 6px 0px rgba(0, 0, 0, 0),
        0px 13px 5px 0px rgba(0, 0, 0, 0.01),
        0px 7px 4px 0px rgba(0, 0, 0, 0.03), 0px 3px 3px 0px rgba(0, 0, 0, 0.04),
        0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    border-radius: 0.75rem;
    overflow-y: auto;

    .search-input {
        border: none;
        border-bottom: 2px solid rgba(25, 93, 165, 0.1);
        padding: 0.5rem 0.75rem;
        font-family: Poppins;
        outline: none;
        font-size: 1rem;

        &::placeholder {
            color: rgba(25, 93, 165, 0.5);
        }
    }

    .no-results {
        padding: 0.75rem;
        color: rgba(25, 93, 165, 0.5);
        font-family: Poppins;
        font-size: 1rem;
    }

    button {
        color: #195da5;
        font-family: Poppins;
        font-size: 1.25rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.5rem;
        letter-spacing: -0.0375rem;
        padding: 0.75rem;
        display: flex;
        transition: 0.1s;

        &:hover {
            background: rgba(25, 93, 165, 0.1);
        }
    }
`;

export const TooltipContainer = styled.div`
    border-radius: 0.375rem;
    background: rgba(0, 0, 0, 0.7);
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(4px);
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;

    color: #fff;
    font-family: Poppins;
    font-size: 0.8125rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.1375rem;
`;

type IdType = string | number;

export const Dropdown = <T extends IdType>({
    children,
    ids,
    divProps,
    setCurrentId,
    icon,
    hideArrow,
    setFormValue,
    floatingProps,
    nullRenderer,
    searchItems,
}: {
    children: ReactNode[];
    setCurrentId: (id: T) => void;
    ids: T[];
    divProps: DivProps;
    icon?: ReactNode;
    hideArrow?: boolean;
    setFormValue?: (value: string) => void;
    floatingProps?: DivProps;
    nullRenderer?: string;
    searchItems?: string[];
}) => {
    const childRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<Instance>();
    const [current, setCurrent] = useState<ReactNode>(
        nullRenderer ?? children[0]
    );
    const [isOpen, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const searchBarRef = useRef<HTMLInputElement>(null);

    const normalize = (s: string) =>
        s
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

    const filteredIdx = useMemo(() => {
        if (!searchItems || query.trim() === "") {
            return children.map((_, i) => i);
        }
        const q = normalize(query.trim());
        return searchItems
            .map((txt, i) => {
                const n = normalize(txt);
                if (n.startsWith(q)) return { i, score: 2 };
                if (n.includes(q)) return { i, score: 1 };
                return { i, score: 0 };
            })
            .filter(({ score }) => score > 0)
            .sort((a, b) => b.score - a.score)
            .map(({ i }) => i);
    }, [children, query, searchItems]);

    const onShow: TippyProps["onShow"] = (instance) => {
        setOpen(true);
        const w = childRef.current ? childRef.current.offsetWidth : 0;
        instance.popper.style.width = `${w}px`;
        if (searchItems) {
            requestAnimationFrame(() => searchBarRef.current?.focus());
        }
    };

    const handleSelect = (idx: number) => {
        setCurrentId(ids[idx]);
        setCurrent(children[idx]);
        setFormValue?.(children[idx] as string);
        setQuery(""); // clear filter
        tooltipRef.current?.hide();
    };

    useEffect(() => {
        if (setFormValue) return;
        if (nullRenderer) {
            setCurrent(nullRenderer);
            return;
        }
        setCurrent(children[0]);
    }, [children.length, nullRenderer, setFormValue]);

    useEffect(() => {
        searchBarRef.current?.focus();
    }, []);

    return (
        <Tippy
            appendTo={document.body}
            placement="bottom"
            animation="shift-away"
            trigger="click"
            interactive
            maxWidth={1500}
            offset={[0, 5]}
            onShow={onShow}
            onHide={() => setOpen(false)}
            onClickOutside={(inst) => inst.hide()}
            onMount={(inst) => (tooltipRef.current = inst)}
            content={
                <Content {...floatingProps}>
                    {searchItems && (
                        <input
                            className="search-input"
                            placeholder="Pesquisar…"
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoFocus={true}
                            ref={searchBarRef}
                            onKeyDown={(e) => {
                                if (
                                    e.key === "Enter" &&
                                    filteredIdx.length === 1
                                ) {
                                    e.preventDefault();
                                    handleSelect(filteredIdx[0]);
                                }
                            }}
                        />
                    )}

                    {filteredIdx.length === 0
                        ? query.length > 0 && (
                              <span className="no-results">
                                  Nenhum resultado para “{query}”
                              </span>
                          )
                        : filteredIdx.map((i) => (
                              <button
                                  key={i}
                                  className="content"
                                  type="button"
                                  onClick={() => handleSelect(i)}
                              >
                                  {children[i]}
                              </button>
                          ))}
                </Content>
            }
        >
            <Container {...divProps} ref={childRef}>
                <button
                    type="button"
                    className={"content" + (isOpen ? " open" : "")}
                >
                    {icon}
                    {current}
                    {!hideArrow && (
                        <motion.span animate={{ rotate: isOpen ? 180 : 0 }}>
                            <LuChevronDown className="arrow" />
                        </motion.span>
                    )}
                </button>
            </Container>
        </Tippy>
    );
};
